import { useEffect, useState } from 'react'
import './SchoolEmployeeEditModal.css'

export type SchoolEmployeeFormData = {
  id?: number
  title: string
  first_name: string
  last_name: string
  title_after: string
  work_email: string
  personal_number: string
  birth_date: string
  phone: string
  position: string
  /** true = aktivní (pracuje), false = pasivní (nepracuje, zůstává v systému) */
  is_active: boolean
}

const emptyForm = (): SchoolEmployeeFormData => ({
  title: '',
  first_name: '',
  last_name: '',
  title_after: '',
  work_email: '',
  personal_number: '',
  birth_date: '',
  phone: '',
  position: '',
  is_active: true,
})

type SchoolEmployeeEditModalProps = {
  isOpen: boolean
  mode: 'create' | 'edit'
  initial: Partial<SchoolEmployeeFormData> | null
  saving?: boolean
  /** IT admin: ruční připojení O365 účtu */
  showItLinkLogin?: boolean
  linkedUserEmail?: string | null
  onClose: () => void
  onSave: (data: SchoolEmployeeFormData) => void | Promise<void>
}

export default function SchoolEmployeeEditModal({
  isOpen,
  mode,
  initial,
  saving = false,
  showItLinkLogin = false,
  linkedUserEmail = null,
  onClose,
  onSave,
}: SchoolEmployeeEditModalProps) {
  const [form, setForm] = useState<SchoolEmployeeFormData>(emptyForm())
  const [linkEmail, setLinkEmail] = useState('')
  const [linking, setLinking] = useState(false)
  const [linkMsg, setLinkMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setForm({
      ...emptyForm(),
      ...initial,
      title: initial?.title ?? '',
      first_name: initial?.first_name ?? '',
      last_name: initial?.last_name ?? '',
      title_after: initial?.title_after ?? '',
      work_email: initial?.work_email ?? '',
      personal_number: initial?.personal_number ?? '',
      birth_date: initial?.birth_date ?? '',
      phone: initial?.phone ?? '',
      position: initial?.position ?? '',
      is_active: initial?.is_active !== false,
      id: initial?.id,
    })
    setLinkEmail(initial?.work_email ?? linkedUserEmail ?? '')
    setLinkMsg(null)
  }, [isOpen, initial, mode, linkedUserEmail])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [isOpen, saving, onClose])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.first_name.trim() && !form.last_name.trim()) {
      alert('Vyplňte alespoň jméno nebo příjmení')
      return
    }
    if (mode === 'create' && !form.personal_number.trim()) {
      alert('Osobní číslo je povinné u nového zaměstnance')
      return
    }
    onSave(form)
  }

  const handleItLinkLogin = async () => {
    if (!form.id || !linkEmail.trim()) {
      alert('Uložte kartu a zadejte e-mail O365 účtu')
      return
    }
    setLinking(true)
    setLinkMsg(null)
    try {
      const apiUrl = window.location.origin + '/api'
      const res = await fetch(`${apiUrl}/employees?action=link-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: form.id, login_email: linkEmail.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setLinkMsg(typeof data.error === 'string' ? data.error : 'Připojení selhalo')
        return
      }
      setLinkMsg(typeof data.message === 'string' ? data.message : 'Účet připojen')
    } catch {
      setLinkMsg('Síťová chyba při připojení účtu')
    } finally {
      setLinking(false)
    }
  }

  return (
    <div className="school-employee-edit-overlay" onClick={saving ? undefined : onClose} role="presentation">
      <div
        className="school-employee-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="school-employee-edit-title"
      >
        <header className="school-employee-edit-modal__header">
          <h2 id="school-employee-edit-title">
            {mode === 'create' ? 'Nový zaměstnanec' : 'Upravit zaměstnance'}
          </h2>
          <button type="button" className="school-employee-edit-modal__close" onClick={onClose} disabled={saving} aria-label="Zavřít">
            ×
          </button>
        </header>

        <form className="school-employee-edit-form" onSubmit={handleSubmit}>
          <div className="school-employee-edit-form__grid">
            <label className="school-employee-edit-field">
              <span>Titul</span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Mgr., Ing."
                className="school-employee-edit-input"
              />
            </label>
            <label className="school-employee-edit-field">
              <span>Jméno</span>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="school-employee-edit-input"
                required={!form.last_name.trim()}
              />
            </label>
            <label className="school-employee-edit-field">
              <span>Příjmení</span>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="school-employee-edit-input"
                required={!form.first_name.trim()}
              />
            </label>
            <label className="school-employee-edit-field">
              <span>Titul za jménem</span>
              <input
                type="text"
                value={form.title_after}
                onChange={(e) => setForm({ ...form, title_after: e.target.value })}
                placeholder="Ph.D."
                className="school-employee-edit-input"
              />
            </label>
            <label className="school-employee-edit-field school-employee-edit-field--wide">
              <span>Pracovní e-mail (O365)</span>
              <input
                type="email"
                value={form.work_email}
                onChange={(e) => setForm({ ...form, work_email: e.target.value })}
                placeholder="jmeno@iss-cheb.cz"
                className="school-employee-edit-input"
              />
            </label>
            <label className="school-employee-edit-field">
              <span>{mode === 'create' ? 'Osobní číslo *' : 'Osobní číslo'}</span>
              <input
                type="text"
                value={form.personal_number}
                onChange={(e) => setForm({ ...form, personal_number: e.target.value })}
                className="school-employee-edit-input"
                required={mode === 'create'}
              />
            </label>
            <label className="school-employee-edit-field">
              <span>Datum narození</span>
              <input
                type="date"
                value={form.birth_date}
                onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                className="school-employee-edit-input"
              />
            </label>
            <label className="school-employee-edit-field">
              <span>Telefon</span>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="school-employee-edit-input"
              />
            </label>
            <label className="school-employee-edit-field school-employee-edit-field--wide">
              <span>Pozice</span>
              <input
                type="text"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                className="school-employee-edit-input"
              />
            </label>
            <label className="school-employee-edit-field school-employee-edit-field--wide school-employee-edit-check">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <span>
                Aktivní zaměstnanec
                <em>
                  {form.is_active
                    ? ' — zobrazuje se v seznamu, pracuje ve škole'
                    : ' — pasivní: nepracuje, zůstává v systému (lze znovu aktivovat)'}
                </em>
              </span>
            </label>
          </div>

          <p className="school-employee-edit-hint">
            Pracovní e-mail musí být adresa Office 365 (@iss-cheb.cz). Nemá-li zaměstnanec školní e-mail,
            pole nechte prázdné — identifikace probíhá přes osobní číslo. U nového záznamu je osobní
            číslo povinné; u stávajících bez čísla může zůstat prázdné.
          </p>

          {showItLinkLogin && mode === 'edit' && form.id ? (
            <div className="school-employee-edit-it-link">
              <p className="school-employee-edit-hint">
                <strong>IT — připojit účet O365</strong>
                {linkedUserEmail ? ` (nyní: ${linkedUserEmail})` : ' (zatím bez vazby)'}
                . Pouze když se účet sám nepřipojil po uložení e-mailu.
              </p>
              <div className="school-employee-edit-form__grid">
                <label className="school-employee-edit-field school-employee-edit-field--wide">
                  <span>E-mail existujícího účtu v intranetu</span>
                  <input
                    type="email"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                    placeholder="jmeno@iss-cheb.cz"
                    className="school-employee-edit-input"
                    disabled={linking || saving}
                  />
                </label>
              </div>
              <button
                type="button"
                className="school-employee-edit-btn school-employee-edit-btn--primary"
                onClick={() => void handleItLinkLogin()}
                disabled={linking || saving || !linkEmail.trim()}
              >
                {linking ? 'Připojuji…' : 'Připojit / nahradit účet O365'}
              </button>
              {linkMsg ? <p className="school-employee-edit-hint">{linkMsg}</p> : null}
            </div>
          ) : null}

          <footer className="school-employee-edit-modal__footer">
            <button type="button" className="school-employee-edit-btn school-employee-edit-btn--ghost" onClick={onClose} disabled={saving}>
              Zrušit
            </button>
            <button type="submit" className="school-employee-edit-btn school-employee-edit-btn--primary" disabled={saving}>
              {saving ? 'Ukládám…' : mode === 'create' ? 'Přidat' : 'Uložit'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}
